import { useState, useEffect } from 'react';
// import copy from 'copy-to-clipboard';
import dayjs from 'dayjs';
import { useMethods } from '@lhb/hook';
import { urlParams } from '@lhb/func';
import { getCookie, setCookie } from '@lhb/cache';
import { getShareBatchCreateShortLink, getShareContent, getShareId } from '@/common/api/common';
import { useTenantInfo } from './useTenantInfo';
import { useCurrentUserInfo } from './useCurrentUserInfo';
import store from '@/store/store';

export interface ShareParams {
  id?: string;
  modelConfigId?: string;
  token?: string;
  failureTime?: string;
  [key: string]: any;
}

export interface UseShareContentOptions {
  /** 过期回调 */
  onExpired?: () => void;
  /** 成功回调 */
  onSuccess?: (params: ShareParams) => void;
  /** 错误回调 */
  onError?: (error: Error) => void;
  /** 是否自动加载 */
  autoLoad?: boolean;
}

/**
 * 获取分享内容
 * @description
 * @return {*}
 * @demo
    const { shareId, shareParams } = useGetShareContent({ onSuccess: (params) => methods.loadData(params) });

    // 分享时调用接口需要的数据，如果有值代表“分享时”
    const shareData = shareId && shareParams ? { tenantId: shareParams.tenantId, userId: shareParams.employeeId } : null;
 */
export const useGetShareContent = (options: UseShareContentOptions = {}) => {
  const {
    onExpired,
    onSuccess,
    onError,
    autoLoad = true
  } = options;

  const [shareId, setShareId] = useState<string>('');
  const [shareParams, setShareParams] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  // 初始化
  useEffect(() => {
    if (autoLoad) {
      const id = methods.getShareIdFromUrl();
      if (id) {
        setShareId(id);
        methods.loadShareContent(id);
      }
    }
  }, [autoLoad]);

  const methods = useMethods({

    /** 从URL获取shareId */
    getShareIdFromUrl: () => {
      const queryParams = urlParams(window.location.search);
      return queryParams?.shareId as string || '';
    },

    /** 加载分享内容 */
    loadShareContent: async (id?: string) => {
      const targetShareId = id || shareId;
      if (!targetShareId) {
        setError('未找到分享ID');
        return null;
      }

      setLoading(true);
      setError('');

      try {
        const res = await getShareContent({ id: targetShareId });
        const { content } = res || {};

        if (!content) {
          throw new Error('分享内容不存在');
        }

        const result: ShareParams = JSON.parse(content);

        // 检查是否过期
        if (result.failureTime && dayjs().isAfter(result.failureTime)) {
          if (onExpired) {
            onExpired();
          } else {
            window.location.href = '/share/failure';
          }
          return null;
        }

        // 设置token
        if (result.token) { // 服务端生成的临时token
          setCookie('flow_share_token', result.token);
        }

        setShareParams(result);

        if (onSuccess) {
          setTimeout(() => { // 等待 token 生效
            onSuccess(result);
          }, 100);
        }

        return result;

      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : '加载分享内容失败';
        setError(errorMsg);

        if (onError) {
          onError(err instanceof Error ? err : new Error(errorMsg));
        }

        return null;

      } finally {
        setLoading(false);
      }
    },

    /** 重新加载 */
    reload: () => {
      return methods.loadShareContent();
    },

    /** 手动加载指定分享 */
    loadById: async (id: string) => {
      setShareId(id);
      return methods.loadShareContent(id);
    },

  });

  return {
    shareId,
    shareParams,
    loading,
    error,
    loadShareContent: methods.reload,
    loadShareContentById: methods.loadById,
    hasShare: !!shareId
  };
};

/**
 * 创建分享内容短链
 * @description 同时支持批量创建分享链接
 * @return {*}
 * @demo
    import copy from 'copy-to-clipboard';

    const { createShareContentLink } = useCreateShareContentLink();

    // 创建分享链接
    const url = await createShareContentLink({ shareUrl: '/expandstore/spotsurveydetail', params: { id: detail?.id } });
    if (url) {
      V2Message.success('链接已复制，可直接发送给你的朋友');
      copy(url);
    }

    // 批量创建分享链接
    const links = await createShareContentLink({ shareUrl: '/expandstore/spotdetail', isBatch: true, batchParams: spotList.map((item) => ({ id: item.id })) });
 */
export const useCreateShareContentLink = () => {

  const {
    /* 获取当前租户信息 */
    getTenantInfo,
  } = useTenantInfo({ immediate: false });

  const {
    /* 获取当前用户信息 */
    getCurrentUserInfo,
  } = useCurrentUserInfo({ immediate: false });

  const methods = useMethods({

    /** 创建分享内容短链 */
    createShareContentLink: async (...rest: [any]) => {
      await Promise.all([getTenantInfo(), getCurrentUserInfo()]);
      return createShareContentLink(...rest);
    }
  });

  return {
    createShareContentLink: methods.createShareContentLink,
  };

};

/**
 * 创建分享内容短链（为了普通函数能够使用）
 * @description 该函数内部不处理获取用户信息的逻辑，请确认是否在此函数前调用了 useTenantInfo 和 useCurrentUserInfo，参考 useCreateShareContentLink
 * @return {*} 分享内容短链：如果 isBatch 为 true，则返回短链数组，如 ['https://example.com/123', 'https://example.com/123']；否则返回单个短链，如 'https://example.com/123'
 * @demo
// 创建分享链接
const link = await createShareContentLink({ shareUrl: '/expandstore/spotdetail', params: { id: item.id } }));

// 批量创建分享链接
const links = await createShareContentLink({ shareUrl: '/expandstore/spotdetail', isBatch: true, batchParams: spotList.map((item) => ({ id: item.id })) });
 */
// TODO(2026-1029迭代/商场慢闪场景体验优化，文档：https://confluence.lanhanba.com/pages/viewpage.action?pageId=172108153)：联调确认短链服务是否读取 content.shareTitle/shareDescription 生成聊天卡片；前端已将两项文案写入短链内容并在落地页设置 title/meta。
export const createShareContentLink = async ({ shareUrl, params, isBatch = false, batchParams, failureTime }: {
    /** 分享链接，注意，pc 和 app 的分享链接需要统一 */
    shareUrl: string,
    /** 分享参数，除了 tenantId, employeeId 外的其他参数，如 { id: '123', modelConfigId: '456' } */
    params?: any,
    /** 是否批量创建分享链接，默认 false；如果为 true，则返回的结果是对象数组 */
    isBatch?: boolean,
    /** 批量创建分享参数，每个元素是一个分享参数对象，仅在 isBatch 为 true 时有效，如 [{ id: '123', modelConfigId: '456' }, { id: '789', modelConfigId: '012' }] */
    batchParams?: any[],
    /**
     * 分享有效期（YYYY-MM-DD HH:mm:ss），未传时默认 30 天。
     * 传入时会同时写入分享内容，供分享落地页读取 content.failureTime 做过期校验
     */
    failureTime?: string,
  }) => {
  if (!shareUrl) {
    throw new Error('分享链接不能为空');
  }
  if (isBatch && !batchParams) {
    throw new Error('批量创建分享参数不能为空');
  } else if (!isBatch && batchParams) {
    throw new Error('分享参数不能为空');
  }

  const state = store.getState().common;
  const tenantInfo: any = state?.tenantInfo;
  const currentUserInfo: any = state?.currentUserInfo;

  const curTime = dayjs();
  // 和app分享统一链接，由原本的7天改为30天
  const expiredTime = failureTime || curTime.add(30, 'day').format('YYYY-MM-DD HH:mm:ss');
  const failureTimeContent = failureTime ? { failureTime } : {};
  const tenantId = getCookie('tenantId') || tenantInfo?.id;
  const employeeId = getCookie('employeeId') || currentUserInfo?.id;
  const globalAliasItemVOs = tenantInfo?.globalAliasItemVOs;
  const api = isBatch ? getShareBatchCreateShortLink : getShareId;

  const commonParams = {
    // 和app分享统一链接
    longLinkUrl: `${process.env.CONSOLE_PC_URL}${shareUrl}`,
    // content: JSON.stringify({
    //   tenantId,
    //   employeeId,
    //   globalAliasItemVOs, // 似乎接口会自行赋值
    //   ...params,
    //   // isSemirModel,
    // }),
    expirationDate: expiredTime, // null代表永不失效
    expirationRedirectUrl: `${process.env.CONSOLE_PC_URL}/share/failure`,
    needToken: true,
  };

  const apiParams = isBatch
    ? {
      dataList: (Array.isArray(batchParams) ? batchParams : []).map(item => ({
        ...commonParams,
        content: JSON.stringify({
          tenantId,
          employeeId,
          globalAliasItemVOs, // 似乎接口会自行赋值
          ...failureTimeContent,
          ...item,
          // isSemirModel,
        }),
      }))
    }
    : {
      ...commonParams,
      content: JSON.stringify({
        tenantId,
        employeeId,
        globalAliasItemVOs, // 似乎接口会自行赋值
        ...failureTimeContent,
        ...params,
      // isSemirModel,
      }),
    };

  return api(apiParams).then((res: any) => {
    return isBatch
      ? (Array.isArray(res) ? res : []).map((item: any) => item?.frontShareLinkUrl || '')
      : res?.frontShareLinkUrl || '';
    // https://gitlab.lanhanba.com/fe/treasure/-/issues/102
    // setTimeout(() => {
    //   V2Message.success('链接已复制，可直接发送给你的朋友');
    //   copy(`${areaName}：${res.frontShareLinkUrl}`);
    // }, 0);
  });
};
